import debounce from 'debounce-promise';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactTable, { Column, DerivedDataObject, ExpandedRows, Instance } from 'react-table-6';
import { getVoiceMailAudioUrl } from '../../api';
import { useSelector } from '../../hook-utils';
import { EventSubscription, VoiceMail, VoiceMailFolder, VoiceMailStatus } from '../../model';
import { actions } from '../../store';
import { selectMyself } from '../../store/reducers/config';
import { selectVoiceMailFolder, shouldFetchVoiceMail } from '../../store/reducers/voiceMail';
import { TableRow } from '../../table-utils';
import { RelativeShortFormatDateTime } from '../CallsPage';
import { ReactComponent as SearchIcon } from '../icons/toolbar-search.svg';
import Input from '../Input';
import { targetFromEvent } from '../Menu';
import MoreButton from '../MoreButton';
import '../SidebarView';
import { TabButton, TabsHeader } from '../Tabs';
import { Audio, FolderNames, getFolderName, VoicemailIcon, VoiceMailPageProps, VoicemailMenuState, VoicemailContextMenu } from '../VoiceMailPage';
import '../VoiceMailPage.scss';
import './VoiceMailPage.scss';


function getColumns(onPlay: (vm: VoiceMail, index: number) => void, onMore: (state: VoicemailMenuState) => void, folder: VoiceMailFolder): Array<Column<VoiceMail>> {
  return [{
    id: 'expander',
    expander: true,
    width: 0,
  }, {
    id: 'icon',
    Header: '',
    width: 34,
    accessor: r => r.status,
    Cell: ({ original: r }) => <VoicemailIcon item={r} />,
  }, {
    id: 'from',
    Header: folder === 'Sent' ? 'To' : 'From',
    accessor: i => i.caller.name ? `${i.caller.name} (${i.caller.id})` : i.caller.id,
  }, {
    id: 'date',
    Header: 'Received',
    accessor: vm => vm.received,
    Cell: ({ original: r }) => <RelativeShortFormatDateTime date={r.received} />,
    className: 'numeric',
    headerClassName: 'numeric',
  }, {
    id: 'menuButton',
    Cell: ({ original: vm, viewIndex }: TableRow<VoiceMail>) => <div className='actions'>

      <MoreButton className='menu-button' onClick={e => {
        e.preventDefault()
        onMore({ event: e, target: targetFromEvent(e), item: vm, index: viewIndex })
      }
      } />
    </div>,
    width: 34,
    sortable: false,
    resizable: false,
  }]
}


export default function VoiceMailPage({ extension, className }: VoiceMailPageProps) {
  const dispatch = useDispatch()
  const [menu, setMenu] = useState<VoicemailMenuState>()
  const me = useSelector(selectMyself)
  const ext = extension || me

  const [folder, setFolder] = useState(FolderNames[0])
  const [urls, setUrls] = useState<Record<string, string>>({})
  const messages = useSelector(s => ext && selectVoiceMailFolder(s, ext, folder))
  const shouldFetch = useSelector(s => ext && shouldFetchVoiceMail(s, ext, folder))
  const [expanded, setExpanded] = useState<ExpandedRows>({});

  const [selected, setSelected] = useState<string>('')
  const [playing, setPlaying] = useState<string>('')

  const [curfilter, setCurFilter] = useState('')
  const [filter, setFilter] = useState('')
  const [data, setData] = useState<VoiceMail[]>([])
  const updateFilter = useMemo(() => debounce(setCurFilter, 1000), [])

  let reactTable = useRef<ReactTable<VoiceMail>>(null);

  useEffect(() => {
    if (ext) {
      dispatch(actions.fetchVoiceMail.request({ mailbox: ext, folder, subscription: EventSubscription.Add }))
    }
  }, [dispatch, folder, ext, shouldFetch])

  useEffect(() => {
    updateFilter(filter)
  }, [filter, updateFilter])

  useEffect(() => {
    setData(filter !== '' && messages ? messages.filter(c => {
      return c.caller.name.toLowerCase().indexOf(filter) > -1 ||
        c.caller.id.toLowerCase().indexOf(filter) > -1 ||
        c.subject.toLowerCase().indexOf(filter) > -1
    }) : messages !== null ? messages : [])
  }, [filter, messages])

  const onPlay = useCallback(async (vm: VoiceMail, index: number) => {
    if (ext === null) { return }

    if (urls[vm.id]) {
      setUrls({})
      setPlaying('')
      dispatch(actions.requestAudioPlaying('', -1))
    } else {
      const url = await getVoiceMailAudioUrl(ext, vm.id, vm.folder)
      setUrls({ [vm.id]: url })
      setPlaying(vm.id)
      dispatch(actions.requestAudioPlaying(url, 0))
    }

  }, [dispatch, ext, urls])

  const f = curfilter.toLowerCase()
  useEffect(() => {
    setData(f !== '' && messages ? messages.filter(c => {
      return c.caller.name.toLowerCase().indexOf(f) > -1 ||
        c.caller.id.toLowerCase().indexOf(f) > -1 ||
        c.subject.toLowerCase().indexOf(f) > -1
    }) : messages !== null ? messages : [])
  }, [f, messages])

  const table = useMemo(() => messages && <ReactTable ref={reactTable}
    className='VoicemailTable'
    data={data}
    columns={getColumns(onPlay, (state) => {
      setMenu(state)
    }, folder)
    } showPagination={true}
    defaultPageSize={20}
    minRows={0}
    NoDataComponent={() => null}
    getTrProps={(_: any, row?: TableRow<VoiceMail>) => row && ({
      className: (row && row.original.status === VoiceMailStatus.Unread ? 'unread ' : ' ') + ((menu && menu.item.id === row.original.id) || (selected.length > 0 && selected === row.original.id) ? ' selected' : undefined),
      onClick(e: React.MouseEvent) {
        setSelected(row.original.id)
      },
    })}
    expanded={expanded}
    onSortedChange={() => {
      setExpanded({})
      setUrls({})
    }}
    ExpanderComponent={() => null}
    SubComponent={(row: TableRow<VoiceMail>) =>
      urls[row.original.id] !== undefined && <Audio play={playing === row.original.id} file={urls[row.original.id]} />
    } />
    , [messages, data, onPlay, folder, expanded, menu, selected, urls, playing])

  useEffect(() => {
    //hack to manage expanded index when the messages change i.e. another one is added
    if (reactTable.current && urls) {
      const table = reactTable.current as Instance
      const sorted: VoiceMail[] = (table.getSortedData(table.getResolvedState()).sortedData as DerivedDataObject[]).map(x => x._original)
      for (let index = 0; index < sorted.length; index++) {
        const element = sorted[index];
        if (urls[element.id] && !expanded[index]) {
          setExpanded({ [index]: true })
        }
      }
    }
    //console.info("messages mount")
    return () => {
      //console.info("messages unmount")
    }
  }, [expanded, messages, table, urls])

  return <>
    <TabsHeader className='tab-buttons'>
      {FolderNames.map((s, i) => <TabButton key={i} active={s === folder} onClick={() => setFolder(s)}>{s}</TabButton>)}
    </TabsHeader>
    <div className='SidebarViewBody'>
      <div className="Header">
        <div className='searchBox'>
          <Input placeholder={getFolderName(folder, 0) === 'Sent' ? 'To / subject' : 'From / subject'} className='search' value={filter} onChange={e => {
            setFilter(e.currentTarget.value)
          }} autocomplete={false} onKeyUp={e => {

          }} />
          <SearchIcon className='searchIcon' />
        </div>
      </div>
      <div className='VoiceMailPage MobileVoiceMailPage'>
        {table}
      </div>
    </div>
    {menu && ext && <VoicemailContextMenu state={menu} selected={[selected]} mailbox={ext} onClose={() => setMenu(undefined)} onPlay={onPlay} />}
  </>
}
